/**
 * Environment Configuration
 *
 * Centralized environment variables with type safety.
 * All values come from .env file - no hardcoded fallbacks.
 * These are injected at build time by Rspack's DefinePlugin.
 *
 * Only vars prefixed with NCODER_PUBLIC_ are exposed to the browser bundle.
 * Never read process.env directly in src/ — add the var here instead.
 */

// Environment type
export type Environment = "local" | "development" | "staging" | "production";

export const env = {
	// Node Environment
	NODE_ENV: process.env.NODE_ENV as "development" | "production",

	// Application Environment
	NCODER_ENV: process.env.NCODER_PUBLIC_ENV as Environment,

	// API Configuration
	API_URL: process.env.NCODER_PUBLIC_API_URL as string,

	// GraphQL
	GRAPHQL_ENDPOINT: process.env.NCODER_PUBLIC_GRAPHQL_ENDPOINT as string,

	// OAuth-specific endpoint
	OAUTH_GRAPHQL_ENDPOINT: process.env
		.NCODER_PUBLIC_OAUTH_GRAPHQL_ENDPOINT as string,

	// WebSocket
	WS_BASE_URL: process.env.NCODER_PUBLIC_WS_BASE_URL as string,

	// Direct SSE endpoint — bypasses the backend proxy for
	// long-lived streaming to avoid proxy receive-timeout / chunk
	// buffering. Hit by `useDevAgentSSE`. If unset, falls back to
	// API_URL/proxy/dev-agent (the same path every other call uses).
	DIRECT_SSE_URL: process.env.NCODER_PUBLIC_DIRECT_SSE_URL as
		| string
		| undefined,

	// Direct testing-service endpoint — bypasses the bearer proxy for the live VNC
	// view (an <iframe>/WebSocket can't send an Authorization header, so it auths
	// with a `?ticket=` against this WS-capable direct URL). Falls back to the
	// proxy base when unset.
	DIRECT_TESTING_URL: process.env.NCODER_PUBLIC_DIRECT_TESTING_URL as
		| string
		| undefined,

	// Optional direct service URLs. When unset, callers must use the
	// authenticated backend proxy under API_URL/proxy/<service>.
	AGENTS_API_URL: process.env.NCODER_PUBLIC_AGENTS_API_URL as
		| string
		| undefined,
	PLAYGROUND_API_URL: process.env.NCODER_PUBLIC_PLAYGROUND_API_URL as
		| string
		| undefined,
	MCP_GATEWAY_API_URL: process.env.NCODER_PUBLIC_MCP_GATEWAY_API_URL as
		| string
		| undefined,
	TESTING_URL: process.env.NCODER_PUBLIC_TESTING_URL as string | undefined,

	// Application Info
	APP_NAME: process.env.NCODER_PUBLIC_APP_NAME as string,
	APP_VERSION: process.env.NCODER_PUBLIC_APP_VERSION as string,

	// Computed Feature Flags
	get isDevelopment() {
		return this.NODE_ENV === "development";
	},

	get isProduction() {
		return this.NODE_ENV === "production";
	},

	get isLocal() {
		return this.NCODER_ENV === "local";
	},

	get showTestSandbox() {
		return this.NCODER_ENV === "local";
	},
} as const;

/**
 * Validate required environment variables at startup.
 * Uses literal process.env references so DefinePlugin can inline them.
 */
export function validateEnv(): void {
	const missing = [
		!process.env.NCODER_PUBLIC_API_URL && "NCODER_PUBLIC_API_URL",
		!process.env.NCODER_PUBLIC_GRAPHQL_ENDPOINT &&
			"NCODER_PUBLIC_GRAPHQL_ENDPOINT",
		!process.env.NCODER_PUBLIC_WS_BASE_URL && "NCODER_PUBLIC_WS_BASE_URL",
		!process.env.NCODER_PUBLIC_ENV && "NCODER_PUBLIC_ENV",
		// Not strictly required (testingAbsoluteUrl falls back to the proxy base),
		// but every deployed build sets it — an unset value in a deploy means
		// browser-loaded testing assets 401 behind the bearer proxy.
		!process.env.NCODER_PUBLIC_DIRECT_TESTING_URL &&
			"NCODER_PUBLIC_DIRECT_TESTING_URL",
	].filter(Boolean) as string[];

	if (missing.length > 0) {
		console.error(
			"❌ Missing required environment variables:",
			missing.join(", "),
			"\nPlease add them to your .env file.",
		);
	}
}

// Auto-validate on import
validateEnv();

export default env;
